import { executeFinanceCommand } from "@/features/finance/lib/retry-safe-command";
import type { Json } from "@/db/types";
import type {
  CreateEstimateLinePayload,
  CreateEstimatePayload,
  CreateEstimatePaymentPayload,
  CreateEstimateResult,
  CreditApplication,
  Estimate,
  EstimateFilters,
  EstimateLine,
  EstimatePaymentResult,
  EstimateProcurementFollowUpTaskMetadata,
  EstimateProcurementSeed,
  ExistingEstimateSummary,
  Product,
  ProductKanbanStatus,
  Task,
  UpdateEstimateLinePayload,
  UpdateEstimatePayload,
} from "@/features/estimates/types";
import { getBackendClient, type QueryBuilder } from "@/lib/backend";
import {
  applyFinanceDocumentSearch,
  applyFinanceListSortedPage,
  archiveFinanceDocument,
  FINANCE_DOCUMENT_LINE_CORE,
  type FinanceDocumentChildDescriptor,
  financeDocumentHeaderSelect,
  financeDocumentQuery,
  readFinanceDocumentChildren,
  resolveFinanceListPage,
  withAppWriteOrigin,
} from "@/lib/db/finance-documents";
import {
  DOCUMENT_TOTAL_FIELDS,
  type DocumentListTotals,
  fetchListTotals,
} from "@/lib/db/list-totals";
import { listProducts } from "@/lib/db/products";
import { createTaskRecord } from "@/lib/db/tasks";
import {
  collectReversedApplicationIds,
  markReversedApplications,
} from "@/lib/credit-application-balances";
import { normalizePaymentSplitResult } from "@/lib/payment-split";
import {
  cast,
  isRecord,
  normalizeDateOnlyInput,
  toPostgrestDayUntil,
  toRangeEndDay,
} from "@/lib/utils/query-helpers";

const ESTIMATE_LINE_SELECT = `
  ${FINANCE_DOCUMENT_LINE_CORE},
  gl_products(cost, sku, product_name, vendor_product_name)
`;

const CREDIT_APPLICATION_SELECT = `
  id, credit_id, estimate_id, invoice_id, amount_applied, is_reversal,
  applied_at, reverses_application_id,
  gl_invoices!gl_credit_applications_invoice_id_fkey ( id, uid ),
  gl_estimates!gl_credit_applications_estimate_id_fkey ( id, uid )
`;

const ESTIMATE_CONVERSION_SELECT = `
  id, invoice_id, conversion_type, transfer_batch_id, credit_transfer_mappings, undo_credit_transfer_mappings,
  converted_at, undone_at, undo_reason
`;

type EstimateDetailResponse = Estimate & {
  lines: EstimateLine[];
  credits?: Estimate["credits"];
  credit_applications?: CreditApplication[];
  conversions?: Estimate["conversions"];
};

const ESTIMATE_INVALID_MESSAGE = "Estimate response was invalid.";

const NUMERIC_STRING_PATTERN = /^-?\d+(\.\d+)?([eE][-+]?\d+)?$/;

/**
 * PostgREST serializes `numeric` columns as strings, so money arrives as either
 * a number or a fully numeric string. Anything else — `"100.50 USD"`, `"12abc"`,
 * `NaN` — is a malformed money value rather than something to coerce, so it
 * resolves to `null` here and fails the read-back guard below.
 */
function toEstimateNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string" && NUMERIC_STRING_PATTERN.test(value.trim())) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isNullableNumeric(value: unknown): boolean {
  return value === null || toEstimateNumber(value) !== null;
}

function isOptionalNumeric(value: unknown): boolean {
  return value === undefined || isNullableNumeric(value);
}

function isNullableBoolean(value: unknown): value is boolean | null {
  return value === null || typeof value === "boolean";
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === "string";
}

function isOptionalNullableString(value: unknown): boolean {
  return value === undefined || isNullableString(value);
}

const ESTIMATE_NULLABLE_STRING_FIELDS = [
  "uid",
  "date",
  "notes",
  "payment_status",
  "detail_completed_at",
  "project_id",
  "converted_date",
  "converted_invoice_id",
  "account_id",
  "created_at",
] as const satisfies readonly (keyof Estimate)[];

const ESTIMATE_NUMERIC_FIELDS = [
  "total_amount",
  "total_paid",
  "balance",
] as const satisfies readonly (keyof Estimate)[];

const ESTIMATE_NULLABLE_BOOLEAN_FIELDS = [
  "is_converted",
  "is_sample",
] as const satisfies readonly (keyof Estimate)[];

function fieldsMatch(
  value: Record<string, unknown>,
  fields: readonly string[],
  predicate: (field: unknown) => boolean,
): boolean {
  return fields.every((field) => predicate(value[field]));
}

function isEstimateAccount(value: unknown): boolean {
  if (value === null) {
    return true;
  }
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    isNullableString(value.name) &&
    typeof value.uid === "string"
  );
}

/**
 * Every column the shared estimate header select returns, paired with the shape
 * it must hold.
 * A table rather than a chain of early returns, so a new column costs one
 * entry and the guard below stays a single pass over the row.
 */
const ESTIMATE_READBACK_CHECKS: readonly {
  fields: readonly string[];
  holds: (value: unknown) => boolean;
}[] = [
  { fields: ["id"], holds: isOptionalString },
  { fields: ESTIMATE_NULLABLE_STRING_FIELDS, holds: isNullableString },
  { fields: ESTIMATE_NUMERIC_FIELDS, holds: isNullableNumeric },
  { fields: ESTIMATE_NULLABLE_BOOLEAN_FIELDS, holds: isNullableBoolean },
  { fields: ["is_quick"], holds: isBoolean },
  { fields: ["pdf_url"], holds: isOptionalNullableString },
  { fields: ["gl_accounts"], holds: isEstimateAccount },
];

/** Validates every field returned by the shared estimate header select. */
function isEstimateReadback(value: unknown): value is Record<string, unknown> {
  if (!isRecord(value)) {
    return false;
  }
  return ESTIMATE_READBACK_CHECKS.every(({ fields, holds }) => fieldsMatch(value, fields, holds));
}

function assertEstimateReadback(value: unknown): Record<string, unknown> {
  if (!isEstimateReadback(value)) {
    throw new Error(ESTIMATE_INVALID_MESSAGE);
  }
  return value;
}

/**
 * Resolves the row's own id, falling back to the one the caller asked for when
 * the response omitted it. A row that reports a *different* id than the one
 * requested is a wrong-row response, not a recoverable omission.
 */
function resolveEstimateId(value: Record<string, unknown>, fallbackId?: string): string {
  const id = typeof value.id === "string" ? value.id : fallbackId;
  if (!id) {
    throw new Error(ESTIMATE_INVALID_MESSAGE);
  }
  if (fallbackId && id !== fallbackId) {
    throw new Error(ESTIMATE_INVALID_MESSAGE);
  }
  return id;
}

/** Money columns as numbers, rejecting anything PostgREST could not have sent. */
function estimateMoney(
  value: Record<string, unknown>,
): Pick<Estimate, "balance" | "total_amount" | "total_paid"> {
  if (!fieldsMatch(value, ESTIMATE_NUMERIC_FIELDS, isOptionalNumeric)) {
    throw new Error(ESTIMATE_INVALID_MESSAGE);
  }
  return {
    balance: toEstimateNumber(value.balance),
    total_amount: toEstimateNumber(value.total_amount),
    total_paid: toEstimateNumber(value.total_paid),
  };
}

/**
 * Narrows any estimate row — command read-back, list page, or detail read — to
 * the two invariants every consumer relies on: an `id` string (`Estimate.id` is
 * non-optional, and callers render `formatDocumentUid(estimate.uid, estimate.id)`)
 * and money fields that are numbers rather than PostgREST numeric strings.
 * `fallbackId` supplies the id when the caller already knows which row it asked
 * for and the response omitted it.
 */
function normalizeEstimateRow(value: unknown, fallbackId?: string): Estimate {
  const row = assertEstimateReadback(value);
  return {
    ...cast<Estimate>(row),
    id: resolveEstimateId(row, fallbackId),
    ...estimateMoney(row),
  };
}

/** Shapes one estimate line for the atomic create/complete RPCs (always a fresh line). */
function buildEstimateLineRpcPayload(lines: CreateEstimateLinePayload[] | undefined): Json {
  return (lines || []).map((line) => ({
    product_name: line.product_name || line.description || null,
    description: line.description || null,
    quantity: line.quantity || 1,
    selling_price: line.selling_price || 0,
    unit_cost: line.unit_cost ?? null,
    product_id: line.product_id || null,
  })) as unknown as Json;
}

/**
 * Shapes one line for `xan_update_estimate_with_payload`'s `p_lines` array. A
 * line carrying `line_id` updates that line (it must belong to the target
 * estimate); a line without it inserts a new line. `product_name`,
 * `quantity`, and `selling_price` are always sent (full replacement, as the
 * line-editor form always has these fields populated); `description` and
 * `product_id` use the RPC's `jsonb ? 'key'` presence check, so they are only
 * included when explicitly provided.
 */
function buildEstimateLineUpsertPayload(line: {
  line_id?: string | null;
  product_name?: string;
  description?: string;
  quantity: number;
  selling_price: number;
  unit_cost?: number | null;
  product_id?: string | null;
}): Json {
  const upsert: Record<string, unknown> = {
    product_name: line.product_name || line.description || null,
    quantity: line.quantity,
    selling_price: line.selling_price,
  };
  if (line.line_id) {
    upsert.line_id = line.line_id;
  }
  if (line.description !== undefined) {
    upsert.description = line.description || null;
  }
  if (line.unit_cost !== undefined) {
    upsert.unit_cost = line.unit_cost;
  }
  if (line.product_id !== undefined) {
    upsert.product_id = line.product_id;
  }
  return upsert as unknown as Json;
}

/** Fetches one estimate by id using the shared select shape. */
async function fetchEstimateById(id: string): Promise<Estimate> {
  const { data, error } = await financeDocumentQuery("estimate").eq("id", id).single();
  if (error) {
    throw new Error(error.message);
  }
  return normalizeEstimateRow(data, id);
}

export interface EstimateListFilters {
  account_id?: EstimateFilters["account_id"];
  date_from?: EstimateFilters["date_from"];
  date_to?: EstimateFilters["date_to"];
  detail_completed?: EstimateFilters["detail_completed"];
  is_quick?: EstimateFilters["is_quick"];
  is_sample?: EstimateFilters["is_sample"];
  limit?: EstimateFilters["limit"];
  offset?: EstimateFilters["offset"];
  search?: EstimateFilters["search"];
  sort_direction?: EstimateFilters["sort_direction"];
  sort_field?: EstimateFilters["sort_field"];
  status?: EstimateFilters["status"];
}

/**
 * `is_sample` is nullable, so "not a sample" has to include the null rows —
 * an equality test would hide them from the quotes scope entirely.
 */
function applySampleFilter<T>(
  query: QueryBuilder<T>,
  isSample: EstimateListFilters["is_sample"],
): QueryBuilder<T> {
  if (isSample === true) {
    return query.eq("is_sample", true);
  }
  if (isSample === false) {
    return query.or("is_sample.is.null,is_sample.eq.false");
  }
  return query;
}

/**
 * `detail_completed_at` is a nullable timestamp, not a flag: "still awaiting
 * detail" is `IS NULL`. An equality test against a boolean matches no row at
 * all, so the needs-detail slice would render empty rather than narrow.
 *
 * Every estimate carries the nullable column, but only quick estimates ever
 * get it set — a detailed estimate's timestamp stays null forever, so the
 * pending half also pins `is_quick = true`, matching both the spec's
 * needs-detail slice and the `estimate_list_totals` RPC the footer runs.
 */
function applyDetailCompletedFilter<T>(
  query: QueryBuilder<T>,
  detailCompleted: EstimateListFilters["detail_completed"],
): QueryBuilder<T> {
  if (detailCompleted === true) {
    return query.not("detail_completed_at", "is", null);
  }
  if (detailCompleted === false) {
    return query.eq("is_quick", true).is("detail_completed_at", null);
  }
  return query;
}

/**
 * The filters the estimate list narrows by, applied in one place so the list,
 * the export and the totals RPC cannot select three different sets.
 *
 * Search matches the `search_text` computed column, which reaches uid, notes,
 * status, amounts and the customer's name and uid in a single request. It
 * replaces a two-step search that first fetched up to 100 matching accounts and
 * folded their ids into the filter: that capped the customer half of the search
 * at 100 accounts, and an id list is not something the totals function could
 * reproduce, so the two would have diverged by construction.
 */
function applyEstimateListFilters<T>(
  query: QueryBuilder<T>,
  params: EstimateListFilters,
): QueryBuilder<T> {
  let next = query;

  if (params.status) {
    next = next.eq("payment_status", params.status);
  }
  if (params.account_id) {
    next = next.eq("account_id", params.account_id);
  }
  if (params.date_from) {
    next = next.gte("date", params.date_from.split("T")[0]);
  }
  if (params.date_to) {
    next = next.lt("date", toPostgrestDayUntil(params.date_to));
  }
  next = applySampleFilter(next, params.is_sample);
  if (params.is_quick !== undefined) {
    next = next.eq("is_quick", params.is_quick);
  }
  next = applyDetailCompletedFilter(next, params.detail_completed);
  next = applyFinanceDocumentSearch(next, params.search);

  return next;
}

/**
 * Lists estimates with optional status, account, date, and text filters.
 *
 * Ordering is a server concern: the list renders one page at a time, so a sort
 * applied after the fetch would only reshuffle that page. `sort_field` comes
 * from the caller's own column definitions and is validated before it reaches
 * the URL, and an absent field keeps the newest-first default. `orderListPage`
 * closes the order with the unique tiebreaker a paged read needs.
 */
export async function getEstimates(params?: EstimateListFilters): Promise<Estimate[]> {
  let query = applyFinanceListSortedPage(
    financeDocumentQuery("estimate"),
    {
      ascending: params?.sort_direction === "asc",
      column: params?.sort_field ?? "date",
    },
    resolveFinanceListPage(params, 50),
  );

  query = applyEstimateListFilters(query, params ?? {});

  const { data, error } = await query;
  if (error) {
    throw new Error(error.message);
  }

  return cast<unknown[]>(data || []).map((row) => normalizeEstimateRow(row));
}

/** Narrows an ISO timestamp filter to the date the RPC compares, or null. */
function toRpcDate(value: string | undefined): string | null {
  return value ? value.split("T")[0] : null;
}

/** The day the list closes on, read the way `getEstimates` reads it, or null. */
function toRpcEndDay(value: string | undefined): string | null {
  return value ? toRangeEndDay(value) : null;
}

/** Trims a search filter down to the text the RPC matches, or null. */
function toRpcSearch(value: string | undefined): string | null {
  return value?.trim() || null;
}

/**
 * Count and money totals for the whole filtered set, from the server.
 *
 * Takes the same filters `getEstimates` applies, so the strip under the table
 * and the rows in it describe one set rather than two.
 */
export async function getEstimateTotals(
  params: EstimateListFilters = {},
): Promise<DocumentListTotals> {
  return fetchListTotals(
    "estimate_list_totals",
    {
      p_account_id: params.account_id || null,
      p_date_from: toRpcDate(params.date_from),
      p_date_to: toRpcEndDay(params.date_to),
      p_detail_completed: params.detail_completed ?? null,
      p_is_quick: params.is_quick ?? null,
      p_is_sample: params.is_sample ?? null,
      p_search: toRpcSearch(params.search),
      p_status: params.status || null,
    },
    DOCUMENT_TOTAL_FIELDS,
  );
}

/**
 * Child relations of the estimate detail read, resolved by the shared port.
 * The tables are estimate-specific; the fan-out, scoping, and first-error
 * semantics are not.
 */
const ESTIMATE_DETAIL_CHILDREN: readonly FinanceDocumentChildDescriptor[] = [
  {
    from: () => getBackendClient().data.from("gl_estimate_lines").select(ESTIMATE_LINE_SELECT),
    order: { ascending: true, column: "created_at" },
    parentColumn: "estimate_id",
  },
  {
    from: () =>
      getBackendClient()
        .data.from("gl_customer_credits")
        .select("id, uid, amount, date, method, reference, account_id, estimate_id"),
    order: { ascending: false, column: "date" },
    parentColumn: "estimate_id",
  },
  {
    from: () =>
      getBackendClient().data.from("gl_credit_applications").select(CREDIT_APPLICATION_SELECT),
    order: { ascending: false, column: "applied_at" },
    parentColumn: "estimate_id",
  },
  {
    from: () =>
      getBackendClient().data.from("gl_estimate_conversions").select(ESTIMATE_CONVERSION_SELECT),
    order: { ascending: false, column: "converted_at" },
    parentColumn: "estimate_id",
  },
];

/** Fetches one estimate with lines, credits, and credit applications. */
export async function getEstimate(id: string): Promise<EstimateDetailResponse> {
  const backend = getBackendClient();
  const headerPromise = backend.data
    .from("gl_estimates")
    .select("*, gl_accounts!gl_estimates_account_id_fkey ( id, name, uid, type )")
    .eq("id", id)
    .maybeSingle();
  const childrenPromise = readFinanceDocumentChildren(id, ESTIMATE_DETAIL_CHILDREN);
  const { data: estimate, error: estimateError } = await headerPromise;

  if (estimateError) {
    void childrenPromise.catch(() => undefined);
    throw new Error(estimateError.message);
  }
  if (!estimate) {
    void childrenPromise.catch(() => undefined);
    throw new Error("Estimate not found");
  }

  const header = normalizeEstimateRow(estimate, id);

  const [lines, credits, applicationRows, conversions] = await childrenPromise;

  const applications = cast<CreditApplication[]>(applicationRows ?? []);

  return {
    ...header,
    lines: cast<EstimateLine[]>(lines ?? []),
    credits: cast<Estimate["credits"]>(credits ?? []),
    credit_applications: markReversedApplications(
      applications,
      collectReversedApplicationIds(applications),
    ),
    conversions: cast<Estimate["conversions"]>(conversions ?? []),
  };
}

/**
 * Creates or merges an estimate for the requested account and date.
 *
 * Header resolution, line inserts, and the recalculation that follows commit
 * as one database transaction via `xan_create_estimate_with_payload`, so a
 * failure partway never leaves a partial estimate. Bound to an idempotency
 * key so a lost response followed by a caller-driven retry cannot duplicate
 * lines; pass `payload.idempotency_key` back in to retry the *same* logical
 * submit, or omit it (the common case) to let this generate a fresh key.
 */
export async function createEstimate(
  payload: CreateEstimatePayload,
): Promise<CreateEstimateResult> {
  const backend = getBackendClient();
  const targetDate = normalizeDateOnlyInput(payload.date ?? null);

  const { data: existingEstimates, error: existingError } = await backend.data
    .from("gl_estimates")
    .select(`
      id, uid, date, total_amount, is_quick, detail_completed_at,
      gl_estimate_lines ( id, product_name, quantity, selling_price, line_total )
    `)
    .eq("account_id", payload.account_id)
    .eq("date", targetDate);
  if (existingError) {
    throw new Error(existingError.message);
  }

  const { data: createResult, error: createError } = await backend.data.rpc(
    "xan_create_estimate_with_payload",
    {
      p_account_id: payload.account_id,
      p_date: targetDate,
      p_notes: payload.notes || null,
      p_is_sample: Boolean(payload.is_sample),
      p_is_quick: Boolean(payload.is_quick),
      p_lines: buildEstimateLineRpcPayload(payload.lines),
      p_idempotency_key: payload.idempotency_key ?? crypto.randomUUID(),
    },
  );

  if (createError) {
    throw new Error(createError.message);
  }
  if (!isRecord(createResult) || typeof createResult.estimate_id !== "string") {
    throw new Error("Estimate create did not return an estimate_id.");
  }

  const completeEstimate = await fetchEstimateById(createResult.estimate_id);

  return {
    ...completeEstimate,
    merged_into_existing: createResult.merged_into_existing === true,
    existing_estimates:
      existingEstimates && existingEstimates.length > 0
        ? cast<ExistingEstimateSummary[]>(existingEstimates)
        : undefined,
  };
}

/** Lists a paginated estimate-line slice for large detail views. */
export async function getEstimateLinesPage(params: {
  estimate_id: string;
  limit?: number;
  offset?: number;
}): Promise<EstimateLine[]> {
  const backend = getBackendClient();

  const query = backend.data
    .from("gl_estimate_lines")
    .select(ESTIMATE_LINE_SELECT)
    .eq("estimate_id", params.estimate_id);
  const { data, error } = await applyFinanceListSortedPage(
    query,
    { ascending: true, column: "created_at" },
    resolveFinanceListPage(params, 100),
  );

  if (error) {
    throw new Error(error.message);
  }

  return cast<EstimateLine[]>(data || []);
}

/**
 * Converts an eligible estimate into an invoice through one idempotent RPC.
 *
 * The RPC returns a jsonb result object; a network retry of a completed
 * conversion comes back with `already_converted: true` and the same
 * `invoice_id`, which this function treats as success. The estimate readback
 * is gone — the detail and list caches refresh via invalidation instead.
 */
export async function convertEstimateToInvoice(
  estimateId: string,
  options?: { idempotency_key?: string },
): Promise<{ invoice_id: string }> {
  const backend = getBackendClient();
  const { data: rpcResult, error: rpcError } = await backend.data.rpc(
    "convert_estimate_to_invoice",
    {
      p_estimate_id: estimateId,
      p_idempotency_key: options?.idempotency_key ?? crypto.randomUUID(),
    },
  );
  if (rpcError) {
    throw new Error(`Conversion failed: ${rpcError.message}`);
  }
  if (!isRecord(rpcResult) || typeof rpcResult.invoice_id !== "string") {
    throw new Error("Conversion did not return an invoice id.");
  }

  return { invoice_id: rpcResult.invoice_id };
}

/**
 * Undoes a conversion through the backend RPC.
 *
 * `xan_undo_estimate_conversion` refuses (via its gauntlet) when the converted
 * invoice carries submitted/processed/PDF artifacts, active shares,
 * post-conversion payments or credit activity, or changed line items. Those
 * refusals surface verbatim from the RPC error so the confirm dialog can show
 * the operator exactly why the undo could not proceed.
 */
export async function undoEstimateConversion(
  estimateId: string,
  options?: { reason?: string },
): Promise<{ success: boolean; invoice_id: string }> {
  const backend = getBackendClient();
  const { data: rpcResult, error: rpcError } = await backend.data.rpc(
    "xan_undo_estimate_conversion",
    {
      p_estimate_id: estimateId,
      p_reason: options?.reason ?? null,
    },
  );
  if (rpcError) {
    throw new Error(rpcError.message);
  }
  if (
    !isRecord(rpcResult) ||
    rpcResult.success !== true ||
    typeof rpcResult.invoice_id !== "string"
  ) {
    throw new Error("Undo did not return a valid result.");
  }
  return {
    success: rpcResult.success === true,
    invoice_id: rpcResult.invoice_id,
  };
}

/**
 * Marks a quick estimate as detail-complete after downstream review.
 *
 * Line inserts, the notes update, and the completion side effects commit as
 * one transaction via `xan_complete_estimate_detail_with_payload`, and an
 * exact replay of the same idempotency key returns the original result
 * instead of duplicating lines or completion side effects.
 */
export async function completeEstimateDetail(
  estimateId: string,
  payload: {
    lines: CreateEstimateLinePayload[];
    notes?: string;
    idempotency_key?: string;
  },
): Promise<Estimate> {
  const backend = getBackendClient();
  const { data: completionResult, error: completionError } = await backend.data.rpc(
    "xan_complete_estimate_detail_with_payload",
    {
      p_estimate_id: estimateId,
      p_lines: buildEstimateLineRpcPayload(payload.lines),
      p_notes: payload.notes ?? null,
      p_idempotency_key: payload.idempotency_key ?? crypto.randomUUID(),
    },
  );
  if (completionError) {
    throw new Error(completionError.message);
  }
  if (!isRecord(completionResult)) {
    throw new Error("Estimate detail completion did not return a result.");
  }

  return fetchEstimateById(estimateId);
}

/**
 * Saves a full estimate edit (header + line upserts/deletes, and detail
 * completion when applicable) as one atomic command via
 * `xan_update_estimate_with_payload`.
 *
 * The idempotency key is caller-provided so a retry of the same command replays
 * one key; generating it here would make every attempt a new command and defeat
 * server-side replay de-duplication.
 */
export async function saveEstimateEdit(
  estimateId: string,
  payload: {
    account_id?: string;
    date?: string | null;
    notes?: string | null;
    is_sample?: boolean;
    lines: Array<{
      line_id?: string | null;
      product_name?: string;
      description?: string;
      quantity: number;
      selling_price: number;
      unit_cost?: number | null;
      product_id?: string | null;
    }>;
    deleted_line_ids: string[];
    complete_detail?: boolean;
    idempotency_key: string;
  },
): Promise<Estimate> {
  if (!payload.idempotency_key.trim()) {
    throw new Error("Estimate edit idempotency key is required.");
  }

  const backend = getBackendClient();

  const { data: editResult, error: editError } = await backend.data.rpc(
    "xan_update_estimate_with_payload",
    {
      p_estimate_id: estimateId,
      p_account_id: payload.account_id ?? null,
      p_date: payload.date ? normalizeDateOnlyInput(payload.date) : null,
      p_notes: payload.notes ?? null,
      p_is_sample: payload.is_sample ?? null,
      p_lines: payload.lines.map(buildEstimateLineUpsertPayload),
      p_deleted_line_ids: payload.deleted_line_ids,
      p_complete_detail: Boolean(payload.complete_detail),
      p_idempotency_key: payload.idempotency_key,
    },
  );

  if (editError) {
    throw new Error(editError.message);
  }
  if (!isRecord(editResult) || typeof editResult.estimate_id !== "string") {
    throw new Error("Estimate edit did not return an estimate_id.");
  }

  return fetchEstimateById(editResult.estimate_id);
}

/** Deletes an estimate that has not been converted. */
export function deleteEstimate(
  estimateId: string,
): Promise<{ success: boolean; archive_id: string }> {
  return archiveFinanceDocument("estimate", estimateId);
}

/** Updates editable estimate metadata for an unconverted estimate. */
export async function updateEstimate(
  id: string,
  payload: UpdateEstimatePayload,
): Promise<Estimate> {
  const backend = getBackendClient();
  const { data: estimate, error: estimateError } = await backend.data
    .from("gl_estimates")
    .select("id, converted_invoice_id")
    .eq("id", id)
    .maybeSingle();
  if (estimateError) {
    throw new Error(estimateError.message);
  }
  if (!estimate) {
    throw new Error("Estimate not found");
  }
  if (estimate.converted_invoice_id) {
    throw new Error("Cannot update a converted estimate");
  }

  const updates: Record<string, unknown> = {};
  if (payload.account_id !== undefined) {
    updates.account_id = payload.account_id;
  }
  if (payload.date !== undefined) {
    updates.date = normalizeDateOnlyInput(payload.date);
  }
  if (payload.is_sample !== undefined) {
    updates.is_sample = payload.is_sample;
  }
  if (payload.notes !== undefined) {
    updates.notes = payload.notes;
  }

  const { data, error } = await backend.data
    .from("gl_estimates")
    .update(withAppWriteOrigin(updates))
    .eq("id", id)
    .select(financeDocumentHeaderSelect("estimate"))
    .single();

  if (error) {
    if (error.code === "23505") {
      throw new Error("An estimate already exists for this account and date.");
    }
    throw new Error(error.message);
  }

  return normalizeEstimateRow(data, id);
}

/** Adds line items to an unconverted estimate. */
export async function addEstimateLines(
  estimateId: string,
  lines: CreateEstimateLinePayload[],
): Promise<EstimateLine[]> {
  const backend = getBackendClient();
  const { data: estimate, error: estimateError } = await backend.data
    .from("gl_estimates")
    .select("id, date, converted_invoice_id")
    .eq("id", estimateId)
    .maybeSingle();
  if (estimateError) {
    throw new Error(estimateError.message);
  }
  if (!estimate) {
    throw new Error("Estimate not found");
  }
  if (estimate.converted_invoice_id) {
    throw new Error("Cannot add line items to a converted estimate");
  }

  const lineInserts = lines.map((line) =>
    withAppWriteOrigin({
      estimate_id: estimateId,
      product_name: line.product_name || line.description || null,
      description: line.description || null,
      quantity: line.quantity || 1,
      selling_price: line.selling_price || 0,
      unit_cost: line.unit_cost ?? null,
      product_id: line.product_id || null,
      date_of_sale: estimate.date || normalizeDateOnlyInput(null),
    }),
  );

  const { data, error } = await backend.data
    .from("gl_estimate_lines")
    .insert(lineInserts)
    .select(ESTIMATE_LINE_SELECT);
  if (error) {
    throw new Error(error.message);
  }

  return cast<EstimateLine[]>(data || []);
}

/** Updates one line item on an unconverted estimate. */
export async function updateEstimateLine(
  estimateId: string,
  lineId: string,
  payload: UpdateEstimateLinePayload,
): Promise<EstimateLine> {
  const backend = getBackendClient();
  const { data: estimate, error: estimateError } = await backend.data
    .from("gl_estimates")
    .select("id, converted_invoice_id")
    .eq("id", estimateId)
    .maybeSingle();
  if (estimateError) {
    throw new Error(estimateError.message);
  }
  if (!estimate) {
    throw new Error("Estimate not found");
  }
  if (estimate.converted_invoice_id) {
    throw new Error("Cannot update line items on a converted estimate");
  }

  const updates: Record<string, unknown> = {};
  if (payload.product_name !== undefined) {
    updates.product_name = payload.product_name;
  }
  if (payload.description !== undefined) {
    updates.description = payload.description;
  }
  if (payload.quantity !== undefined) {
    updates.quantity = payload.quantity;
  }
  if (payload.selling_price !== undefined) {
    updates.selling_price = payload.selling_price;
  }
  if (payload.unit_cost !== undefined) {
    updates.unit_cost = payload.unit_cost;
  }
  if (payload.product_id !== undefined) {
    updates.product_id = payload.product_id;
  }

  const { data, error } = await backend.data
    .from("gl_estimate_lines")
    .update(withAppWriteOrigin(updates))
    .eq("id", lineId)
    .eq("estimate_id", estimateId)
    .select(ESTIMATE_LINE_SELECT)
    .maybeSingle();
  if (error) {
    throw new Error(error.message);
  }
  if (!data) {
    throw new Error("Estimate line not found");
  }

  return cast<EstimateLine>(data);
}

/** Deletes one line item from an unconverted estimate. */
export async function deleteEstimateLine(
  estimateId: string,
  lineId: string,
): Promise<{ deleted: boolean }> {
  const backend = getBackendClient();
  const { data: estimate, error: estimateError } = await backend.data
    .from("gl_estimates")
    .select("id, converted_invoice_id")
    .eq("id", estimateId)
    .maybeSingle();
  if (estimateError) {
    throw new Error(estimateError.message);
  }
  if (!estimate) {
    throw new Error("Estimate not found");
  }
  if (estimate.converted_invoice_id) {
    throw new Error("Cannot delete line items from a converted estimate");
  }

  const { data, error } = await backend.data
    .from("gl_estimate_lines")
    .delete()
    .eq("id", lineId)
    .eq("estimate_id", estimateId)
    .select("id");
  if (error) {
    throw new Error(error.message);
  }
  if (!data || data.length === 0) {
    throw new Error("Estimate line not found");
  }

  return { deleted: true };
}

/** Records an estimate payment and returns its normalized credit split. */
export async function createEstimatePayment(
  estimateId: string,
  payload: CreateEstimatePaymentPayload,
): Promise<EstimatePaymentResult> {
  const backend = getBackendClient();
  const normalizedPaymentDate = normalizeDateOnlyInput(payload.date ?? null);

  const command = await executeFinanceCommand("estimate_payment", {
    document_id: estimateId,
    amount: payload.amount,
    date: normalizedPaymentDate,
    method: payload.method || "Cash",
    reference: payload.reference || null,
  });

  const normalizedSplit = normalizePaymentSplitResult(command.result, {
    requestedAmount: payload.amount,
    estimateId,
    fallbackRemainderCreditAmount: 0,
  });

  if (!normalizedSplit.raw) {
    throw new Error("Estimate payment did not return a valid result.");
  }

  const { applicationId, creditId } = normalizedSplit.ids;

  if (!creditId) {
    throw new Error("Customer credit ID was not returned.");
  }

  const { data: credit, error: creditError } = await backend.data
    .from("gl_customer_credits")
    .select("id, uid, amount, date, method, reference, estimate_id, account_id")
    .eq("id", creditId)
    .single();
  if (creditError) {
    throw new Error(creditError.message);
  }

  let application: CreditApplication | null = null;
  if (applicationId) {
    const { data: applicationRow, error: applicationError } = await backend.data
      .from("gl_credit_applications")
      .select(CREDIT_APPLICATION_SELECT)
      .eq("id", applicationId)
      .single();
    if (!applicationError && applicationRow) {
      application = cast<CreditApplication>(applicationRow);
    }
  }

  command.acknowledge();
  return {
    credit: cast<EstimatePaymentResult["credit"]>(credit),
    application,
    split: normalizedSplit.split,
  };
}

/**
 * Lists products available to estimate lines with optional search, kanban
 * status, and purchase-order filters.
 */
export function getProducts(params?: {
  search?: string;
  status?: ProductKanbanStatus;
  purchase_order_id?: string;
  limit?: number;
  offset?: number;
}): Promise<Product[]> {
  return listProducts<Product>(params);
}

/** Resolves the project used for estimate procurement follow-up tasks. */
export async function getEstimatesProjectId(): Promise<string> {
  const backend = getBackendClient();
  const { data, error } = await backend.data.rpc("xan_get_estimates_project");

  if (error) {
    throw new Error(error.message);
  }
  if (!data || typeof data !== "string") {
    throw new Error("Field Estimates project could not be resolved.");
  }

  return data;
}

/**
 * Creates the procurement follow-up task seeded from an estimate line draft.
 * Retrying the same `commandId` with the same draft returns the first task.
 */
export async function createEstimateProcurementFollowUpTask(payload: {
  accountId?: string | null;
  category?: string | null;
  commandId: string;
  cost: number;
  estimateId: string;
  estimateLineName: string;
  lineId: string;
  linkedProductId?: string | null;
  notes?: string | null;
  profileId: string;
  vendorProductName: string;
}): Promise<Task> {
  const projectId = await getEstimatesProjectId();

  const procurementSeed: EstimateProcurementSeed = {
    estimate_line_name: payload.estimateLineName,
    vendor_product_name: payload.vendorProductName,
    category: payload.category ?? null,
    cost: payload.cost,
    notes: payload.notes ?? null,
  };

  const metadata: EstimateProcurementFollowUpTaskMetadata = {
    source: "estimate-procurement-followup",
    estimate_id: payload.estimateId,
    client_line_id: payload.lineId,
    linked_product_id: payload.linkedProductId ?? null,
    procurement_seed: procurementSeed,
    requires_po_completion: true,
  };

  return createTaskRecord(
    payload.commandId,
    {
      title: `Procurement: ${procurementSeed.vendor_product_name || procurementSeed.estimate_line_name}`,
      description:
        "Follow up on procurement details for this estimate line and complete downstream PO/product work.",
      status: "todo",
      priority: "medium",
      tags: ["estimate", "procurement-seed", "auto-generated"],
      account_id: payload.accountId ?? undefined,
      project_id: projectId,
      related_estimate_id: payload.estimateId,
      metadata: metadata as unknown as Record<string, unknown>,
    },
    payload.profileId,
  );
}
